/**
 * Provider search filter options (string[] arrays mapped inline by filter components).
 * Form select options ({ value, label }[]) live in common-form-options.ts.
 */

// ─── Age range ────────────────────────────────────────────────────────────────

export const THERAPY = "Therapy";

export const AgeRangeOptions = [
  "Early Childhood (0-5)",
  "Children (6-11)",
  "Adolescents (12-17)",
  "Young Adult (18-29)",
  "Adult (30-64)",
  "Older Adult (65+)",
];

export const UNDER_12_AGE_RANGES = AgeRangeOptions.slice(0, 2); // 'Early Childhood (0-5)', 'Children (6-11)'
export const UNDER_18_AGE_RANGES = AgeRangeOptions.slice(0, 3); // + 'Adolescents (12-17)'

// ─── Provider search filters ──────────────────────────────────────────────────

export const ProviderGenderOptions = ["Male", "Female"];

export const SessionTypeOptions = ["In-person", "Virtual"];

export const MODALITY = {
  IN_PERSON: "in-person",
  TELEHEALTH: "telehealth",
  NO_PREFERENCE: "no preference",
} as const;

export type Modality = (typeof MODALITY)[keyof typeof MODALITY];

export const WeekdayOptions = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export const ProviderRadiusOptions = ["5 miles", "10 miles", "25 miles", "35 miles", "No limit"];

export const CareFormatOptions = ["Individual", "Couples", "Family"];

export const ServiceTypeRequiredReasonForVisitOptions: ReadonlyArray<string> = [
  "Family Stress / Conflict",
  "Marital / Partner Conflict",
  "Relationship Stressors",
  "Marital/partner conflict",
];

// Legacy (pre-update) list, used when FEATURE_UPDATED_REASON_FOR_VISIT is disabled.
export const LegacyServiceTypeRequiredReasonForVisitOptions: ReadonlyArray<string> = [
  "Divorce",
  "Family Stress / Conflict",
  "Interpersonal Conflict",
  "Marital Conflict",
  "Relationship Issues",
];

// Union of both lists — used for flag-agnostic membership checks (e.g. deciding whether
// a persisted/selected speciality requires the service-type care-format checkbox).
export const AllServiceTypeRequiredReasonForVisitOptions: ReadonlyArray<string> = [
  ...new Set([
    ...ServiceTypeRequiredReasonForVisitOptions,
    ...LegacyServiceTypeRequiredReasonForVisitOptions,
  ]),
];

export const ServiceTypeOptions = [
  "Therapy",
  "Medication Management",
  "Therapy & Medication Management",
];

export const CLINICIAN_TYPE = {
  PRESCRIBER: "Prescriber",
  THERAPIST: "Therapist",
} as const;

export type ClinicianType = (typeof CLINICIAN_TYPE)[keyof typeof CLINICIAN_TYPE];

export const CARE_FORMAT = {
  IN_PERSON: MODALITY.IN_PERSON,
  TELEHEALTH: MODALITY.TELEHEALTH,
} as const;

export type CareFormat = (typeof CARE_FORMAT)[keyof typeof CARE_FORMAT];

// ─── Slot modality label ──────────────────────────────────────────────────────

/**
 * Modality label constants for slot availability
 * These represent the combined modalities available for appointment slots
 */
export const MODALITY_LABEL = {
  /** Slot available for both virtual and in-person appointments */
  VIRTUAL_AND_IN_PERSON: "Virtual and in-person",

  /** Slot available for virtual appointments only */
  VIRTUAL: "Virtual",

  /** Slot available for in-person appointments only */
  IN_PERSON: "In-person",
} as const;

export type ModalityLabel = (typeof MODALITY_LABEL)[keyof typeof MODALITY_LABEL];

export type SpecialityMatchType = "and" | "or";

// ─── Speciality filter ────────────────────────────────────────────────────────

export const FilteredSpecialistOptions = [
  "ADHD / Executive Functioning / Learning Difficulties",
  "Adoption-Related Concerns",
  "Anger Management",
  "Anxiety (worry, social anxiety, or panic experiences)",
  "Autism Spectrum Disorder",
  "Behavioral Addiction (gambling, gaming, shopping)",
  "Behavioral Disorders",
  "Bipolar I",
  "Bipolar II",
  "Christian-based counseling",
  "Chronic Medical Condition",
  "College, Career & Academic Transition Concerns",
  "Conduct Disorders",
  "Coping Skills",
  "Cultural Identity and Experiences",
  "Depression",
  "Eating or Body Image Concerns",
  "Family Stress / Conflict",
  "First Responders",
  "Gender Affirming Care / Gender Identity",
  "Grief, Loss and Bereavement",
  "LGBTQIA+ Life Experiences",
  "Major Life Events / Transitions",
  "Marital / Partner Conflict",
  "Men's Health",
  "Neurodevelopmental Disorders",
  "Obsessive Thoughts or Repetitive Behaviors (OCD)",
  "Personality Disorders",
  "Phobias",
  "Pregnancy and Postpartum Mental Health",
  "Recent Psychiatric Hospitalization / Residential Treatment (14 days or less)",
  "Relationship Stressors",
  "Self Esteem / Self Identity",
  "Self-Harm",
  "Severe Mental Illness (psychosis, thought dysfunction)",
  "Somatization Disorders",
  "Spirituality and Faith-related concerns",
  "Stress Management / Stage of Life",
  "Substance Use Concerns (Current)",
  "Substance Use Concerns in Remission (Recovery)",
  "Suicidal Ideation / Behavior (Active)",
  "Suicidal Ideation / Behavior without a plan to harm oneself (Passive)",
  "Trauma or Overwhelming Experiences",
  "Veteran / Military and Family Life",
  "Women's Health",
];

// Legacy (pre-update) list, used when FEATURE_UPDATED_REASON_FOR_VISIT is disabled.
export const LegacyFilteredSpecialistOptions = [
  "ADHD / Executive Functioning / Learning Difficulties",
  "Adoption-Related Concerns",
  "Anger Management",
  "Anxiety",
  "Autism Spectrum Disorder",
  "Behavioral Addiction (gambling, gaming, shopping)",
  "Behavioral Disorders",
  "BiPolar I and II",
  "Chronic Illness",
  "Cognitive Impairment (Dementia / Brain Injury)",
  "Conduct Disorders",
  "Coping Skills",
  "Depression",
  "Eating or Body Image Concerns",
  "Family Stress / Conflict",
  "Gender Affirming Care / Gender Identity",
  "Grief, Loss and Bereavement",
  "LGBTQIA+ Life Experiences",
  "Major Life Events / Transitions",
  "Marital / Partner Conflict",
  "Men's Health",
  "Neurodevelopmental Disorders",
  "Obsessive Thoughts or Repetitive Behaviors (OCD)",
  "Personality Disorders",
  "Phobias",
  "Pregnancy and Postpartum Mental Health",
  "Recent Psychiatric Hospitalization / Residential Treatment (45 days or less)",
  "Relationship Stressors",
  "Self Esteem / Self Identity",
  "Self-Harm",
  "Severe Mental Illness (psychosis, thought dysfunction)",
  "Somatization Disorders",
  "Stress Management / Stage of Life",
  "Substance Use Concerns (Current)",
  "Substance Use Concerns in Remission (Recovery)",
  "Suicidal Ideation / Behavior (Active)",
  "Suicidal Ideation / Behavior (Passive)",
  "Trauma or Overwhelming Experiences",
  "Veteran",
  "Women's Health",
];
